import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';

import { MapType, ViewUpdateListener, WorldPoint2 } from '@strava/mre';

import { useMRE } from '../../hooks/useMRE';
import { useMREState } from '../../hooks/useMREState';
import type { LatLng } from '../../types';
import { calculateScrubberTranslatePixelValues } from './Scrubber.utils';

import styles from './Scrubber.module.scss';

interface ScrubberProps {
  latLng: LatLng;
}

/*
 * And HTML scrubber rendered on TOP of the canvas, not by the MRE.
 * Why? To fulfil the product requirement of allowing the scrubber to render on top of POI markers,
 * as it's an easier change then allowing the configuration of complex intersection rules in the MRE.
 */
export const Scrubber = ({ latLng }: ScrubberProps) => {
  const { terrainEngine, mapContainerRef } = useMRE();
  const { mapType } = useMREState();

  /*
   * Performance Optimisation 1: We will set the `transform` style on the DOM element directly via a ref, rather
   * than using the react render loop. This prevents the position from lagging behind where it should be
   * when the camera is moving fast.
   */
  const scrubberRef = useRef<HTMLDivElement>(null);

  const className = useMemo(() => {
    // These are MRE styles for which "dark_polyline_scrub" is preferred to "light_polyline_scrub"
    const useDarkIconStyle = mapType
      ? [
          MapType.STRAVA_SATELLITE_SUMMER,
          MapType.STRAVA_HYBRID,
          MapType.STRAVA_TOPO_DARK
        ].includes(mapType)
      : false;

    return clsx(
      styles.scrubber,
      useDarkIconStyle ? styles.scrubberDark : styles.scrubberLight
    );
  }, [mapType]);

  const updateScrubber = useCallback(
    (worldPosition: WorldPoint2) => {
      if (!terrainEngine || !mapContainerRef.current || !scrubberRef.current) {
        return;
      }
      const scrubberTranslatePixelValues =
        calculateScrubberTranslatePixelValues(
          terrainEngine,
          mapContainerRef.current,
          worldPosition
        );

      if (scrubberTranslatePixelValues) {
        scrubberRef.current.style.transform = `translateX(${scrubberTranslatePixelValues.x}px) translateY(${scrubberTranslatePixelValues.y}px) translateX(-50%) translateY(-50%)`;
        scrubberRef.current.style.display = 'initial';
      } else {
        scrubberRef.current.style.display = 'none';
      }
    },
    [terrainEngine, mapContainerRef] // ref added to deps array to keep linter happy - has no functional impact
  );

  /*
   * Performance Optimisation 2: Rather than reattach and cleanup event handlers to listen to view update events on
   * every `latLng` change (which happens a lot as the mouse if moving) we do it just once. To enable this, we need
   * a way to access the "current" `latLng` values from within the ViewUpdateListener, which we do with a utility
   * ref which we keep in sync with the props.
   */
  const lastLatLng = useRef<LatLng>(latLng);

  /*
   * This effect updates the scrubber position when we want to render it at a different world location.
   */
  useEffect(() => {
    updateScrubber({
      latitude: latLng[0],
      longitude: latLng[1]
    });

    lastLatLng.current = latLng;
  }, [latLng, updateScrubber]);

  /*
   * This effect updates the scrubber position after the camera position changes in any way.
   * Note: We could add/remove new event listeners within the useEffect above, but that effect runs
   * often (as the mouse moves) and it is a performance optimisation to not do that.
   * Therefore, we only attach the event listeners once, and use a ref to track the last world position
   * value at which the scrubber should be placed.
   */
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const viewUpdateListener: ViewUpdateListener = {
      onViewUpdated: () => {
        updateScrubber({
          latitude: lastLatLng.current[0],
          longitude: lastLatLng.current[1]
        });
      }
    };

    terrainEngine.addViewUpdateListener(viewUpdateListener);

    return () => {
      terrainEngine.removeViewUpdateListener(viewUpdateListener);
    };
  }, [terrainEngine, updateScrubber]);

  return (
    <div
      data-testid="mre-scrubber"
      className={className}
      ref={scrubberRef}
      style={{
        // As we are skipping the react render cycle and updating the `transform` style directly in effects,
        // we can't choose to just "not render" the scrubber when we don't want to show it (due to being occluded
        // behind terrain, for example). Therefore, we set default to `display: none` which we will control at the
        // same time as setting the translate values.
        display: 'none'
      }}
    ></div>
  );
};
